const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://marketmate-kmn1.onrender.com/api/v1';

export interface GiftItem {
  name: string;
  quantity: number;
  deliveryClass?: string;
  isPerishable?: boolean;
  estimatedWeightKg?: number;
}

export interface GiftClaimDetails {
  orderId: string;
  orderNumber: string;
  recipientName: string;
  giftMessage?: string;
  senderDisplayName: string;
  isAnonymous: boolean;
  items: GiftItem[];
  sellerName?: string;
  sellerCity?: string;
  status: string;
  requiresRecipientAddress: boolean;
  isClaimed: boolean;
  expiresAt?: string;
}

export interface SubmitGiftAddressPayload {
  deliveryAddress: {
    street: string;
    city: string;
    state?: string;
    coordinates: [number, number]; // [lng, lat]
  };
  notes?: string;
}

export interface GiftApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
}

export class GiftService {
  /**
   * Fetch gift claim details by token
   */
  static async getClaimDetails(token: string): Promise<GiftApiResponse<GiftClaimDetails>> {
    try {
      const response = await fetch(`${API_BASE_URL}/gifts/claim/${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const json = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: json.message || 'Unable to retrieve gift package details. The link may be invalid or expired.',
          data: json.data,
        };
      }

      return {
        success: true,
        message: json.message || 'Gift details retrieved successfully',
        data: json.data,
      };
    } catch (error: any) {
      console.error('[GiftService] Error fetching claim details:', error);
      return {
        success: false,
        message: 'Network error. Please check your internet connection and try again.',
      };
    }
  }

  /**
   * Submit delivery address to claim gift
   */
  static async submitClaimAddress(
    token: string,
    payload: SubmitGiftAddressPayload,
  ): Promise<GiftApiResponse<GiftClaimDetails>> {
    try {
      const response = await fetch(`${API_BASE_URL}/gifts/claim/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const json = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: json.message || 'Failed to submit delivery address. Please check your details and try again.',
          data: json.data,
        };
      }

      return {
        success: true,
        message: json.message || 'Gift package claimed successfully! Your delivery is being prepared.',
        data: json.data,
      };
    } catch (error: any) {
      console.error('[GiftService] Error submitting claim address:', error);
      return {
        success: false,
        message: 'Network error. Please check your internet connection and try again.',
      };
    }
  }
}
